/**
 * O que faz: Converte os dados carregados no dashboard em informações estáveis para o relatório.
 * Onde usa: DashboardReportDocument nos formatos A4 e Mobile.
 * Conexões: DashboardMetrics, leads filtrados, apelidos de anúncios e dashboardReportAnalytics.
 * Tabelas: Representa agregações já obtidas de leads, agendamentos e chat_mensagens.
 * Data: 27/07/2026.
 */

import type { DashboardMetrics } from '@/hooks/dashboard/types';
import type { Lead } from '@/hooks/useSupabaseLeads';
import type { ResumoDashboard } from '@/features/dashboard/data/dashboardContracts';
import { buildDailyOriginSeries, buildWeekdaySeries } from './dashboardReportAnalytics';
import type { DashboardReportRankingItem } from './dashboardReportTypes';

export interface DashboardReportModelInput {
  metrics: DashboardMetrics;
  leads: Lead[];
  clinicName: string;
  periodLabel: string;
  startDate: Date | null;
  endDate: Date | null;
  generatedAt: Date;
  getAdDisplayName: (adName: string) => string;
  /** Resumo compacto preferido pelo novo dashboard; leads permanecem para compatibilidade de testes legados. */
  resumo?: ResumoDashboard;
}

export interface DashboardReportModel {
  clinicName: string;
  periodLabel: string;
  startDate: Date | null;
  endDate: Date | null;
  generatedAt: Date;
  kpis: {
    totalContacts: number;
    adLeads: number;
    exclusiveLinkLeads: number;
    sentMessages: number;
    receivedMessages: number;
    scheduledAppointments: number;
    completedAppointments: number;
    convertedContacts: number;
    conversionRate: number;
    paidRevenue: number;
  };
  responseTime: {
    overall: string;
    human: string;
    ai: string;
    businessHours: string;
    distribution: {
      ate30min: number;
      de30mina1h: number;
      de1ha4h: number;
      acimaDe4h: number;
    } | null;
  };
  originConversion: DashboardMetrics['conversaoPorOrigem'];
  dailySeries: ReturnType<typeof buildDailyOriginSeries>;
  weekdaySeries: ReturnType<typeof buildWeekdaySeries>;
  adRanking: DashboardReportRankingItem[];
  linkRanking: DashboardReportRankingItem[];
  insights: string[];
  methodology: string;
}

const buildRate = (conversions: number, leads: number) =>
  leads > 0 ? Math.round((conversions / leads) * 1000) / 10 : 0;

export const buildDashboardReportModel = ({
  metrics,
  leads,
  clinicName,
  periodLabel,
  startDate,
  endDate,
  generatedAt,
  getAdDisplayName,
  resumo,
}: DashboardReportModelInput): DashboardReportModel => {
  // Mantém o atendimento, que é carregado separadamente, e troca os totais analíticos pelo resumo quando disponível.
  const metricasDoRelatorio = resumo?.metricas;
  // O PDF recebe a mesma série limitada da tela. Assim não recria nem baixa milhares de leads só para exportar.
  const dailySeries = resumo ? resumo.serie.map((item) => ({
    dateKey: item.inicio === item.fim ? item.inicio : `${item.inicio}/${item.fim}`,
    label: item.inicio === item.fim ? item.inicio : `${item.inicio}–${item.fim}`,
    total: item.todos,
    ads: item.anuncios,
    links: item.links_exclusivos,
    other: item.outros,
    isInterval: item.inicio !== item.fim,
  })) : buildDailyOriginSeries(leads, startDate, endDate, 90);
  const weekdaySeries = resumo ? buildWeekdaySeriesFromSummary(resumo) : buildWeekdaySeries(leads);
  const bestWeekday = weekdaySeries.reduce((best, item) => item.leads > best.leads ? item : best, weekdaySeries[0]);
  const peakDay = dailySeries.reduce((best, item) => item.total > best.total ? item : best, dailySeries[0] ?? {
    dateKey: '', label: '', total: 0, ads: 0, links: 0, other: 0,
  });
  const response = metrics.tempoMedioResposta;

  const adRanking = (resumo ? resumo.anuncios.itens : metrics.leadsPorAnuncio).slice(0, 10).map((item) => ({
    name: 'nome' in item && item.apelido ? item.apelido : getAdDisplayName('nome' in item ? item.nome : item.anuncio),
    leads: item.leads,
    conversions: item.conversoes,
    conversionRate: buildRate(item.conversoes, item.leads),
  }));

  const linkRanking = (resumo ? resumo.links.itens : metrics.leadsPorLink).slice(0, 10).map((item) => ({
    name: 'nome' in item ? item.nome : item.linkName,
    leads: item.leads,
    conversions: item.conversoes,
    conversionRate: buildRate(item.conversoes, item.leads),
  }));

  // Os insights usam apenas agregações do período para que não façam afirmações que o relatório não consegue comprovar.
  const insights = [
    bestWeekday.leads > 0
      ? `${bestWeekday.label} concentrou ${bestWeekday.percentage}% dos contatos.`
      : 'Sem volume suficiente para identificar o melhor dia.',
    peakDay.total > 0
      ? peakDay.isInterval
        ? `O maior volume foi no intervalo ${peakDay.label}, com ${peakDay.total} contatos.`
        : `O pico diário foi em ${peakDay.label}, com ${peakDay.total} contatos.`
      : 'Sem pico no período.',
    adRanking[0]
      ? `${adRanking[0].name} liderou os anúncios com ${adRanking[0].leads} leads.`
      : 'Nenhum anúncio identificado no período.',
  ];

  return {
    clinicName: clinicName.trim() || 'Clínica não identificada',
    periodLabel,
    startDate,
    endDate,
    generatedAt,
    kpis: {
      totalContacts: metricasDoRelatorio?.totalContatos ?? metrics.totalContatos,
      adLeads: metricasDoRelatorio?.leadsAnuncios ?? metrics.leadsAnuncios,
      exclusiveLinkLeads: metricasDoRelatorio?.leadsLinksPersonalizados ?? metrics.leadsLinksPersonalizados,
      sentMessages: metrics.mensagensEnviadas,
      receivedMessages: metrics.mensagensRecebidas,
      scheduledAppointments: metricasDoRelatorio?.consultasAgendadas ?? metrics.consultasAgendadas,
      completedAppointments: metricasDoRelatorio?.consultasRealizadas ?? metrics.consultasRealizadas,
      convertedContacts: metricasDoRelatorio?.contatosConvertidos ?? metrics.contatosConvertidos,
      conversionRate: metricasDoRelatorio?.taxaConversao ?? metrics.taxaConversao,
      paidRevenue: metricasDoRelatorio?.faturamentoRealizado ?? metrics.faturamentoRealizado,
    },
    responseTime: {
      overall: response?.tempoMedioFormatado ?? 'Sem dados',
      human: response?.detalhes.tempoMedioHumanoFormatado ?? 'Sem dados',
      ai: response?.detalhes.tempoMedioIAFormatado ?? 'Sem dados',
      businessHours: response?.detalhes.tempoMedioComercialFormatado ?? 'Sem dados',
      distribution: response?.detalhes.distribuicao ?? null,
    },
    originConversion: metricasDoRelatorio?.conversaoPorOrigem ?? metrics.conversaoPorOrigem,
    dailySeries,
    weekdaySeries,
    adRanking,
    linkRanking,
    insights,
    methodology: 'Conversão = contato criado no período com ao menos um agendamento não cancelado. Faturamento = consultas com status Pago na data do pagamento + vendas do CRM na data da venda. Na divisão por origem, anúncios têm prioridade sobre links. Períodos longos são agrupados em intervalos para preservar a série completa sem sobrecarregar a visualização.',
  };
};

const buildWeekdaySeriesFromSummary = (resumo: ResumoDashboard) => {
  const total = resumo.semana.reduce((soma, item) => soma + item.todos, 0);
  const nomes = [['Domingo', 'Dom'], ['Segunda', 'Seg'], ['Terça', 'Ter'], ['Quarta', 'Qua'], ['Quinta', 'Qui'], ['Sexta', 'Sex'], ['Sábado', 'Sáb']] as const;
  return nomes.map(([label, shortLabel], dayIndex) => {
    const leads = resumo.semana.find(item => item.dia === dayIndex)?.todos ?? 0;
    return { dayIndex, label, shortLabel, leads, percentage: total > 0 ? Math.round((leads / total) * 1000) / 10 : 0 };
  });
};
